/**
 * Leads pipeline kanban (Phase 3B).
 *
 * Six fixed-stage columns:
 *
 *   New → Contacted → Viewing scheduled → Negotiating → Closed Won/Lost
 *
 * Stage transitions go through POST /:id/stage on the backend (which
 * stamps stage_changed_at — important for the "stuck for N days"
 * indicators). Cards can be moved two ways, both routed through the
 * same optimistic handleStageChange: click-to-move (a small popover on
 * each card — the accessible / touch / keyboard path) and native HTML5
 * drag-drop via a dedicated per-card grip handle (a mouse-only
 * progressive enhancement). DnD is isolated to the grip so it can never
 * hijack the card click, the tel: call link, or the move-menu.
 *
 * Mobile: horizontal scroll across columns, each fixed at ~18rem.
 * Desktop: same layout, all columns visible. Lead cards carry only
 * what's actionable (name, temperature, type, listing-of-interest,
 * budget, last activity).
 */

import {
	ArrowLeft,
	CalendarClock,
	CheckCircle2,
	Eye,
	GripVertical,
	Handshake,
	Home,
	Inbox,
	MessageCircle,
	MoreHorizontal,
	Phone,
	Plus,
	RefreshCw,
	Search,
	Sparkles,
	UserPlus,
	Wallet,
	XCircle,
} from "lucide-react";
import type {
	ComponentType,
	DragEvent as ReactDragEvent,
	SVGProps,
} from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ContactInitials } from "@/features/realtor/components/contact-initials";
import {
	changeLeadStage,
	type LeadWithLinks,
	listMyLeads,
	PIPELINE_STAGES,
	type PipelineStage,
} from "@/features/realtor/services/leads-api.service";
import { formatPaiseAsRupees } from "@/features/realtor/utils/listings-format";
import { formatRelativeTime } from "@/features/realtor/utils/relative-time";
import { StudioButton } from "@/features/studio/theme";

type LoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "loaded"; leads: LeadWithLinks[] };

interface StageTheme {
	icon: ComponentType<SVGProps<SVGSVGElement>>;
	rail: string;
	chip: string;
	hint: string;
}

const STAGE_THEME: Record<PipelineStage, StageTheme> = {
	new: {
		icon: Inbox,
		rail: "bg-slate-300 dark:bg-slate-600",
		chip: "bg-dashboard-pill-neutral-bg text-dashboard-ink-2 ring-1 ring-inset ring-dashboard-border-light",
		hint: "new",
	},
	contacted: {
		icon: MessageCircle,
		rail: "bg-blue-400 dark:bg-blue-500",
		chip: "bg-dashboard-info-bg-soft text-dashboard-info-strong ring-1 ring-inset ring-dashboard-info-border",
		hint: "contacted",
	},
	viewing_scheduled: {
		icon: Eye,
		rail: "bg-yellow-400 dark:bg-yellow-500",
		chip: "bg-dashboard-warning-bg-soft text-dashboard-warning ring-1 ring-inset ring-dashboard-warning-border",
		hint: "viewing_scheduled",
	},
	negotiating: {
		icon: Handshake,
		rail: "bg-purple-400 dark:bg-purple-500",
		chip: "bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200 dark:bg-purple-950 dark:text-purple-200 dark:ring-purple-800",
		hint: "negotiating",
	},
	closed_won: {
		icon: CheckCircle2,
		rail: "bg-emerald-500 dark:bg-emerald-600",
		chip: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:ring-emerald-800",
		hint: "closed_won",
	},
	closed_lost: {
		icon: XCircle,
		rail: "bg-slate-300 dark:bg-slate-600",
		chip: "bg-dashboard-pill-neutral-bg text-dashboard-muted-dark ring-1 ring-inset ring-dashboard-border-light",
		hint: "closed_lost",
	},
};

export function LeadsPipelinePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });
	const [searchTerm, setSearchTerm] = useState("");
	const unmountedRef = useRef(false);
	const movingRef = useRef(new Set<string>());

	const runLoad = useCallback(async () => {
		setLoad({ status: "loading" });
		const result = await listMyLeads();
		if (unmountedRef.current) return;
		if (!result.success || !result.data) {
			setLoad({
				status: "error",
				message: result.error ?? t("realtor.leads.loadFailedFallback"),
			});
			return;
		}
		setLoad({ status: "loaded", leads: result.data });
	}, [t]);

	useEffect(() => {
		unmountedRef.current = false;
		runLoad();
		return () => {
			unmountedRef.current = true;
		};
	}, [runLoad]);

	const handleStageChange = async (
		leadId: string,
		nextStage: PipelineStage,
	) => {
		if (load.status !== "loaded") return;
		if (movingRef.current.has(leadId)) return;
		const previousLead = load.leads.find((l) => l.lead.id === leadId)?.lead;
		if (!previousLead) return;
		movingRef.current.add(leadId);

		setLoad((current) => {
			if (current.status !== "loaded") return current;
			return {
				status: "loaded",
				leads: current.leads.map((l) =>
					l.lead.id === leadId
						? {
								...l,
								lead: {
									...l.lead,
									pipelineStage: nextStage,
									stageChangedAt: new Date().toISOString(),
								},
							}
						: l,
				),
			};
		});

		try {
			const result = await changeLeadStage(leadId, nextStage);
			if (!result.success) {
				setLoad((current) => {
					if (current.status !== "loaded") return current;
					return {
						status: "loaded",
						leads: current.leads.map((l) =>
							l.lead.id === leadId ? { ...l, lead: previousLead } : l,
						),
					};
				});
				toast.error(result.error ?? t("realtor.leads.stageChangeFailed"));
				return;
			}
			const serverLead = result.data;
			if (serverLead) {
				setLoad((current) => {
					if (current.status !== "loaded") return current;
					return {
						status: "loaded",
						leads: current.leads.map((l) =>
							l.lead.id === leadId ? { ...l, lead: serverLead } : l,
						),
					};
				});
			}
		} finally {
			movingRef.current.delete(leadId);
		}
	};

	// Native HTML5 drag-drop drop handler. Mouse-only progressive
	// enhancement layered on top of the click-to-move MoveStageMenu
	// (which stays the accessible / touch fallback). It intentionally
	// reuses handleStageChange verbatim so the optimistic-update +
	// rollback + server-reconcile path is identical to click-to-move —
	// no second copy of that logic to drift.
	//
	// Two guards live here, not in handleStageChange:
	//  - same-column drop is a no-op (skip the changeLeadStage call);
	//  - a drop whose lead can't be found (stale board) is ignored.
	// A drop that lands outside any column fires no drop event at all,
	// so the card simply stays put — the "revert to original position"
	// behaviour is inherent, nothing extra to undo.
	const handleDropLead = (leadId: string, targetStage: PipelineStage) => {
		if (load.status !== "loaded") return;
		const current = load.leads.find((l) => l.lead.id === leadId);
		if (!current) return;
		if (current.lead.pipelineStage === targetStage) return;
		handleStageChange(leadId, targetStage);
	};

	const filteredLeads = useMemo(() => {
		if (load.status !== "loaded") return [];
		const term = searchTerm.trim().toLowerCase();
		if (!term) return load.leads;
		return load.leads.filter((entry) => {
			const haystack = [
				entry.lead.name,
				entry.lead.phone,
				entry.lead.email ?? "",
				...entry.listings.map((l) => l.headline),
				...entry.lead.tags,
			]
				.join(" ")
				.toLowerCase();
			return haystack.includes(term);
		});
	}, [load, searchTerm]);

	const leadsByStage = useMemo(() => {
		const map = new Map<PipelineStage, LeadWithLinks[]>();
		for (const stage of PIPELINE_STAGES) {
			map.set(stage.id, []);
		}
		for (const entry of filteredLeads) {
			const list = map.get(entry.lead.pipelineStage) ?? [];
			list.push(entry);
			map.set(entry.lead.pipelineStage, list);
		}
		return map;
	}, [filteredLeads]);

	if (load.status === "loading") {
		return (
			<div
				className="bg-dashboard-surface min-h-screen p-6 space-y-8"
				data-testid="leads-pipeline-loading"
			>
				<Skeleton className="h-40 w-full rounded-xl bg-dashboard-bg" />
				<Skeleton className="h-72 w-full rounded-xl bg-dashboard-bg" />
			</div>
		);
	}

	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3" role="alert">
						<p className="text-sm text-dashboard-muted">{load.message}</p>
						<StudioButton
							variant="secondary"
							className="gap-1.5 border-dashboard-border text-dashboard-text"
							onClick={() => runLoad()}
						>
							<RefreshCw className="h-4 w-4" />
							{t("realtor.dashboard.retry")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	const totalCount = load.leads.length;
	const isEmpty = totalCount === 0;

	return (
		<div className="bg-dashboard-surface min-h-screen p-6 space-y-8">
			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6">
					<button
						type="button"
						onClick={() => navigate("/dashboard")}
						className="flex items-center gap-2 text-dashboard-muted hover:text-dashboard-text transition-colors mb-4"
					>
						<ArrowLeft className="h-4 w-4" />
						<span className="text-sm font-body">Back to dashboard</span>
					</button>

					<p className="text-xs font-mono uppercase tracking-widest text-dashboard-muted">
						Realtor Portal
					</p>
					<h1
						className="font-display text-3xl italic font-medium text-dashboard-text mt-2"
						style={{ lineHeight: 1.1 }}
					>
						Your <span className="text-dashboard-accent-display">Leads</span>
					</h1>
					<p className="text-sm text-dashboard-muted mt-2">
						{isEmpty
							? t("realtor.leads.emptyCount")
							: `${totalCount} ${totalCount === 1 ? "lead" : "leads"} in your pipeline`}
					</p>
				</CardContent>
			</Card>

			{isEmpty && (
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 flex items-start gap-4">
						<div className="relative shrink-0">
							<div
								className="rounded-full p-2.5"
								style={{
									backgroundColor: "var(--color-dashboard-pill-active-bg)",
								}}
							>
								<UserPlus
									className="h-4 w-4 text-dashboard-accent-dark"
									aria-hidden="true"
								/>
							</div>
							<Sparkles
								className="absolute -top-1 -right-1 h-3 w-3 text-dashboard-accent-dark"
								style={{ opacity: 0.7 }}
								aria-hidden="true"
							/>
						</div>
						<div className="flex-1 min-w-0">
							<h2 className="font-display text-base italic font-medium text-dashboard-text">
								{t("realtor.leads.emptyTitle")}
							</h2>
							<p className="text-xs text-dashboard-muted mt-1">
								{t("realtor.leads.emptySubtitle")}
							</p>
						</div>
						<StudioButton
							size="sm"
							className="shrink-0 gap-1.5"
							onClick={() => navigate("/realtor/leads/new")}
						>
							<Plus className="h-4 w-4" />
							{t("realtor.leads.addCta")}
						</StudioButton>
					</CardContent>
				</Card>
			)}

			{!isEmpty && (
				<div className="pb-3">
					<div className="flex items-center gap-3">
						<div className="relative group flex-1">
							<Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-dashboard-muted" />
							<Input
								className="pl-10 h-10 rounded-lg border-dashboard-border bg-dashboard-bg shadow-sm text-sm font-body"
								placeholder={t("realtor.leads.searchPlaceholder")}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
						</div>
						<StudioButton
							size="sm"
							className="shrink-0 gap-1.5"
							onClick={() => navigate("/realtor/leads/new")}
						>
							<Plus className="h-4 w-4" />
							{t("realtor.leads.addCta")}
						</StudioButton>
					</div>
				</div>
			)}

			<div className="overflow-x-auto -mx-4 px-4 pb-4 snap-x snap-proximity scroll-px-4">
				<div className="flex gap-3 min-w-max">
					{PIPELINE_STAGES.map((stage) => {
						const stageLeads = leadsByStage.get(stage.id) ?? [];
						return (
							<KanbanColumn
								key={stage.id}
								stageId={stage.id}
								count={stageLeads.length}
								leads={stageLeads}
								onStageChange={handleStageChange}
								onDropLead={handleDropLead}
								onCardClick={(id) => navigate(`/realtor/leads/${id}`)}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}

// A lead card's drag carries this private type, so the columns react only
// to lead cards: not to files, selected text or links dragged over them.
const LEAD_DRAG_TYPE = "application/x-finishd-lead";

const isLeadDrag = (e: ReactDragEvent): boolean =>
	Array.from(e.dataTransfer?.types ?? []).includes(LEAD_DRAG_TYPE);

interface KanbanColumnProps {
	stageId: PipelineStage;
	count: number;
	leads: LeadWithLinks[];
	onStageChange: (leadId: string, nextStage: PipelineStage) => void;
	onDropLead: (leadId: string, targetStage: PipelineStage) => void;
	onCardClick: (leadId: string) => void;
}

function KanbanColumn({
	stageId,
	count,
	leads,
	onStageChange,
	onDropLead,
	onCardClick,
}: KanbanColumnProps) {
	const { t } = useTranslation();
	const stageLabel = t(`realtor.leads.stages.${stageId}`);
	const theme = STAGE_THEME[stageId];
	const Icon = theme.icon;

	// Drop-zone highlight. dragenter/dragleave fire for every child the
	// cursor crosses (cards inside the well), so we depth-count to avoid
	// the highlight flickering off while still hovering the column.
	const [isDragOver, setIsDragOver] = useState(false);
	const dragDepthRef = useRef(0);

	const resetDragOver = useCallback(() => {
		dragDepthRef.current = 0;
		setIsDragOver(false);
	}, []);

	// Belt and braces: if a dragleave was missed, the drag finishing still
	// clears the column. A drop on a drop target bubbles to the document; so
	// does dragend, unless the dragged card moved column and unmounted first.
	// Each covers a finish the other can miss, which is why both are needed.
	useEffect(() => {
		document.addEventListener("drop", resetDragOver);
		document.addEventListener("dragend", resetDragOver);
		return () => {
			document.removeEventListener("drop", resetDragOver);
			document.removeEventListener("dragend", resetDragOver);
		};
	}, [resetDragOver]);

	return (
		<section
			aria-label={stageLabel}
			data-testid={`kanban-column-${stageId}`}
			onDragEnter={(e) => {
				if (!isLeadDrag(e)) return;
				e.preventDefault();
				dragDepthRef.current += 1;
				setIsDragOver(true);
			}}
			onDragOver={(e) => {
				if (!isLeadDrag(e)) return;
				// preventDefault is required for the element to be a valid
				// drop target (otherwise the browser rejects the drop).
				e.preventDefault();
				if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
			}}
			onDragLeave={(e) => {
				if (!isLeadDrag(e)) return;
				dragDepthRef.current -= 1;
				if (dragDepthRef.current <= 0) resetDragOver();
			}}
			onDrop={(e) => {
				e.preventDefault();
				resetDragOver();
				const leadId = e.dataTransfer?.getData(LEAD_DRAG_TYPE);
				if (leadId) onDropLead(leadId, stageId);
			}}
			className="w-72 shrink-0 flex flex-col snap-start"
		>
			<header className="flex items-center justify-between mb-2 px-1">
				<div className="flex items-center gap-2 min-w-0">
					<Icon
						className="h-3.5 w-3.5 shrink-0 text-dashboard-muted"
						aria-hidden="true"
					/>
					<span className="text-[13px] font-semibold truncate text-dashboard-text">
						{stageLabel}
					</span>
					<span
						className={`rounded-full px-2 py-1 text-[10px] font-semibold leading-none tabular-nums ${theme.chip}`}
					>
						{count}
					</span>
				</div>
			</header>

			<div
				data-drag-over={isDragOver || undefined}
				className={`flex flex-col rounded-xl border overflow-hidden min-h-65 transition-colors ${
					isDragOver
						? "border-dashboard-accent bg-dashboard-accent/10 ring-2 ring-dashboard-accent/40"
						: "border-dashboard-border bg-dashboard-surface"
				}`}
			>
				<div className={`h-0.5 w-full ${theme.rail}`} aria-hidden="true" />
				<div className="flex flex-col gap-2 p-2 flex-1">
					{leads.length === 0 ? (
						<EmptyColumnCell stageId={stageId} />
					) : (
						leads.map((entry) => (
							<LeadCard
								key={entry.lead.id}
								entry={entry}
								onStageChange={onStageChange}
								onClick={() => onCardClick(entry.lead.id)}
							/>
						))
					)}
				</div>
			</div>
		</section>
	);
}

function EmptyColumnCell({ stageId }: { stageId: PipelineStage }) {
	const { t } = useTranslation();
	const theme = STAGE_THEME[stageId];
	const Icon = theme.icon;
	return (
		<div className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed py-8 px-3 text-center border-dashboard-border bg-dashboard-bg">
			<Icon
				className="h-4 w-4 text-dashboard-muted"
				style={{ opacity: 0.6 }}
				aria-hidden="true"
			/>
			<p
				className="text-[11px] leading-snug text-dashboard-muted"
				style={{ opacity: 0.8 }}
			>
				{t(`realtor.leads.stageEmptyHint.${stageId}`)}
			</p>
		</div>
	);
}

interface LeadCardProps {
	entry: LeadWithLinks;
	onStageChange: (leadId: string, nextStage: PipelineStage) => void;
	onClick: () => void;
}

function LeadCard({ entry, onStageChange, onClick }: LeadCardProps) {
	const { t, i18n } = useTranslation();
	const { lead, listings, upcomingActivity } = entry;
	const [isDragging, setIsDragging] = useState(false);
	const budget = formatBudget(lead.budgetMin, lead.budgetMax);
	const lastActivityLabel =
		upcomingActivity?.title ??
		(lead.stageChangedAt
			? t("realtor.leads.stageChangedAt", {
					relative: formatRelativeTime(
						lead.stageChangedAt,
						i18n.language,
						t("realtor.time.justNow"),
					),
				})
			: t("realtor.leads.justAdded"));

	return (
		// biome-ignore lint/a11y/useSemanticElements: pipeline card is a complex interactive container
		<div
			role="button"
			tabIndex={0}
			onClick={onClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onClick();
				}
			}}
			className={`group/card relative rounded-lg border p-3 transition-all duration-150 hover:-translate-y-0.5 cursor-pointer space-y-2 border-dashboard-border bg-dashboard-bg shadow-[var(--dashboard-shadow-1)] ${
				isDragging ? "opacity-50" : ""
			}`}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="flex items-center gap-2 min-w-0">
					<Avatar className="h-8 w-8 border border-dashboard-border">
						<AvatarFallback className="text-[11px] font-semibold bg-dashboard-border text-dashboard-text">
							<ContactInitials name={lead.name} />
						</AvatarFallback>
					</Avatar>
					<div className="min-w-0">
						<p className="text-sm font-semibold leading-tight truncate text-dashboard-text font-body">
							{lead.name}
						</p>
						<p className="text-[10px] capitalize text-dashboard-muted">
							{t(`realtor.leads.types.${lead.type}`)}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-1 shrink-0">
					{/*
					 * Dedicated drag handle. DnD is isolated to this grip so a
					 * drag can never start on the card body, the tel: link, or
					 * the move-menu — that is the #1 regression guard (those
					 * gestures stay fully intact). The handle is aria-hidden +
					 * tabIndex=-1 (mouse-only progressive enhancement); keyboard
					 * / touch / AT users move stages via the MoveStageMenu, so
					 * no new i18n string is required here. onClick stopPropagation
					 * keeps a stray grip click from navigating to lead detail.
					 */}
					<button
						type="button"
						aria-hidden="true"
						tabIndex={-1}
						draggable
						data-testid={`drag-handle-${lead.id}`}
						onClick={(e) => e.stopPropagation()}
						onDragStart={(e) => {
							e.stopPropagation();
							e.dataTransfer.setData(LEAD_DRAG_TYPE, lead.id);
							e.dataTransfer.effectAllowed = "move";
							setIsDragging(true);
						}}
						onDragEnd={() => setIsDragging(false)}
						className="inline-flex items-center justify-center h-7 w-6 rounded-md text-dashboard-muted/50 cursor-grab active:cursor-grabbing hover:text-dashboard-text hover:bg-dashboard-surface transition-colors touch-none"
					>
						<GripVertical className="h-4 w-4" />
					</button>
					<TempBadge temperature={lead.temperature} />
					<MoveStageMenu
						currentStage={lead.pipelineStage}
						onMove={(stage) => onStageChange(lead.id, stage)}
					/>
				</div>
			</div>

			{(listings.length > 0 || budget) && (
				<div className="space-y-1">
					{listings.length > 0 && (
						<div className="flex items-center gap-1.5 text-[11px] min-w-0 text-dashboard-muted">
							<Home className="h-3 w-3 shrink-0" aria-hidden="true" />
							<span className="truncate">
								{listings[0].bhk != null && `${listings[0].bhk} BHK · `}
								{listings[0].locality}
							</span>
						</div>
					)}
					{budget && (
						<div className="flex items-center gap-1.5 text-[11px] min-w-0 text-dashboard-muted">
							<Wallet className="h-3 w-3 shrink-0" aria-hidden="true" />
							<span className="truncate tabular-nums">{budget}</span>
						</div>
					)}
				</div>
			)}

			<div className="flex items-center justify-between pt-2 border-t text-[10px] gap-2 border-dashboard-border">
				<span className="flex items-center gap-1 truncate flex-1 min-w-0 text-dashboard-muted">
					<CalendarClock className="h-3 w-3 shrink-0" aria-hidden="true" />
					<span className="truncate">{lastActivityLabel}</span>
				</span>
				<a
					href={`tel:${lead.phoneCountryCode}${lead.phone}`}
					draggable={false}
					onClick={(e) => e.stopPropagation()}
					className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors"
					style={{
						backgroundColor: "var(--color-dashboard-pill-active-bg)",
						color: "var(--color-dashboard-accent-dark)",
					}}
					aria-label={t("realtor.leads.callAriaLabel", {
						name: lead.name,
					})}
				>
					<Phone className="h-3 w-3" aria-hidden="true" />
					{t("realtor.leads.callShort")}
				</a>
			</div>
		</div>
	);
}

function TempBadge({ temperature }: { temperature: "hot" | "warm" | "cold" }) {
	const config = TEMP_CONFIG[temperature];
	return (
		<span
			className="inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-medium capitalize shrink-0"
			style={{
				backgroundColor: config.bgColor,
				color: config.textColor,
			}}
		>
			<span
				className="h-1.5 w-1.5 rounded-full"
				style={{ backgroundColor: config.dotColor }}
				aria-hidden="true"
			/>
			{temperature}
		</span>
	);
}

const TEMP_CONFIG: Record<
	"hot" | "warm" | "cold",
	{ bgColor: string; textColor: string; dotColor: string }
> = {
	hot: {
		bgColor: "var(--color-dashboard-pill-risk-bg)",
		textColor: "var(--color-dashboard-pill-risk)",
		dotColor: "var(--color-dashboard-pill-risk)",
	},
	warm: {
		bgColor: "var(--color-dashboard-pill-awaiting-bg)",
		textColor: "var(--color-dashboard-pill-awaiting)",
		dotColor: "var(--color-dashboard-pill-awaiting)",
	},
	cold: {
		bgColor: "var(--color-dashboard-pill-info-bg)",
		textColor: "var(--color-dashboard-pill-info)",
		dotColor: "var(--color-dashboard-pill-info)",
	},
};

interface MoveStageMenuProps {
	currentStage: PipelineStage;
	onMove: (stage: PipelineStage) => void;
}

function MoveStageMenu({ currentStage, onMove }: MoveStageMenuProps) {
	const { t } = useTranslation();
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					onClick={(e) => e.stopPropagation()}
					className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-dashboard-border bg-dashboard-bg text-dashboard-muted hover:text-dashboard-text hover:bg-dashboard-surface transition-colors"
					aria-label={t("realtor.leads.moveStageAriaLabel")}
				>
					<MoreHorizontal className="h-4 w-4" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				onClick={(e) => e.stopPropagation()}
				className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]"
			>
				<DropdownMenuLabel
					className="text-xs"
					style={{
						fontFamily: "var(--font-mono)",
						fontSize: 10,
						fontWeight: 600,
						letterSpacing: "0.15em",
						textTransform: "uppercase",
						color: "var(--color-dashboard-muted)",
					}}
				>
					{t("realtor.leads.moveStageMenuLabel")}
				</DropdownMenuLabel>
				<DropdownMenuSeparator className="border-dashboard-border" />
				{PIPELINE_STAGES.filter((s) => s.id !== currentStage).map((stage) => (
					<DropdownMenuItem
						key={stage.id}
						onSelect={() => onMove(stage.id)}
						className="text-sm text-dashboard-text hover:bg-dashboard-surface focus:bg-dashboard-surface cursor-pointer"
					>
						{t(`realtor.leads.stages.${stage.id}`)}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function formatBudget(min: number | null, max: number | null): string | null {
	if (min == null && max == null) return null;
	if (min != null && max != null) {
		return `${formatPaiseAsRupees(min)} – ${formatPaiseAsRupees(max)}`;
	}
	if (min != null) return `≥ ${formatPaiseAsRupees(min)}`;
	if (max != null) return `≤ ${formatPaiseAsRupees(max)}`;
	return null;
}
